import AppShell from "../components/AppShell";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { BookOpen, Play, Sparkles, FileText, Users, Briefcase, Compass, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { LaunchPrepButton } from "../components/LaunchPrepButton";
import BookExpertModal from "../components/founder/BookExpertModal";
import { api } from "../lib/api";
import { startFounderTour, resetFounderTour, resetProjectIntakeTour, resetProjectReportTour } from "../lib/tour";

const NAV = [
  {to:"/founder/dashboard",label:"Dashboard"},
  {to:"/founder/tutorial",label:"Tutorial"},
  {to:"/founder/team",label:"Team"},
  {to:"/onboarding",label:"Onboarding"},
];

const PROJECT_NAV = [
  { to: "/project/intake", label: "Project Intake" },
  { to: "/project/report", label: "Readiness Report" },
  { to: "/founder/team", label: "Team" },
  { to: "/founder/tutorial", label: "Tutorial" },
];

const CHAPTERS = [
  { num: "01", icon: FileText, title: "Complete your Business Snapshot",
    body: "We pre-fill your sector, headquarters and summary from your answers and website — review it, pick your revenue stage and tell us why you're raising. Everything stays editable." },
  { num: "02", icon: Sparkles, title: "Upload the four key docs",
    body: "Pitch deck · financial model · 3 years of management accounts · core legals. Each one feeds into your Clinq Capital Readiness Score™ (CCR Score™)." },
  { num: "03", icon: Briefcase, title: "Get your readiness score",
    body: "A 0–100 score plus a transparent component breakdown. You'll know exactly what to fix to climb." },
  { num: "04", icon: Users, title: "Unlock funder matches",
    body: "Once your dashboard is complete, you see your top-matched funders by type (Equity, Debt, Mezzanine, Grant) — and Clinq can do the intros on your behalf." },
];

const PROJECT_CHAPTERS = [
  { num: "01", icon: FileText, title: "Complete your Project Intake",
    body: "Work through the guided intake — sponsor track record, land & permits, technical readiness, revenue security, capital stack and risk. The more you complete, the firmer your score." },
  { num: "02", icon: Sparkles, title: "Upload project evidence",
    body: "Feasibility study · land title · permits & EIA · offtake agreements · EPC contract · sponsor accounts. The checklist adapts to your project stage — required docs are flagged first." },
  { num: "03", icon: Briefcase, title: "Get your Project Readiness Score",
    body: "A 0–100 PRS across six pillars, weighted for your target funder type (bank debt, DFI, equity or grant). Hard checks — like unresolved land title — cap the score until fixed." },
  { num: "04", icon: Users, title: "Get your report & get verified",
    body: "The full report adds strengths, gaps, a 90-day roadmap and funder verdicts. Strong projects (PRS 55+) can get a hand-reviewed Verified badge and be connected to investors." },
];

const SESSION_STATUS_META = {
  pending_payment: { label: "Payment pending", cls: "bg-amber-100 text-amber-800" },
  requested:       { label: "Requested — we'll confirm by email", cls: "bg-sky-100 text-sky-800" },
  confirmed:       { label: "Confirmed", cls: "bg-emerald-100 text-emerald-800" },
  completed:       { label: "Completed", cls: "bg-neutral-200 text-neutral-700" },
  cancelled:       { label: "Cancelled", cls: "bg-red-100 text-red-800" },
};

export default function FounderTutorial() {
  // Prep hand-off prefills — company snapshot + which funder docs are still
  // missing, so "Launch Clinq Prep" opens the builder pre-configured.
  const [prep, setPrep] = useState({ company: "", sector: "", stage: "", missing: [] });
  const [isProject, setIsProject] = useState(false);
  // Expert session booking state
  const [bookOpen, setBookOpen] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("book") === "1") setBookOpen(true);
  }, []);
  const [sessions, setSessions] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get("/expert-sessions/mine");
      setSessions(data.sessions || []);
    } catch (_) { /* non-blocking */ }
  }, []);

  useEffect(() => {
    (async () => {
      const [meRes, cRes] = await Promise.allSettled([
        api.get("/companies/me"),
        api.get("/companies/me/completion"),
      ]);
      const comp = meRes.status === "fulfilled" ? (meRes.value.data || {}) : {};
      const checks = cRes.status === "fulfilled" ? (cRes.value.data?.checks || {}) : {};
      setIsProject((comp.kind || "business") === "project");
      setPrep({
        company: comp.company_name || "",
        sector: comp.sector || "",
        stage: comp.revenue_stage || "",
        missing: ["pitch_deck", "financial_model"].filter((d) => !checks[`${d}_uploaded`]),
      });
    })();
    loadSessions();
  }, [loadSessions]);

  // Paystack return: callback_url is /founder/tutorial?expert_booking=<id>
  // and Paystack appends &reference=<ref>. Verify-on-return so the booking
  // flips to "requested" even before the webhook lands.
  useEffect(() => {
    const bookingId = searchParams.get("expert_booking");
    const reference = searchParams.get("reference") || searchParams.get("trxref");
    if (!bookingId || !reference) return;
    (async () => {
      try {
        const { data } = await api.post(`/expert-sessions/poll/${reference}`);
        if (data.payment_status === "paid") {
          toast.success("Payment received! Your session is booked — we'll confirm the time by email.");
        } else if (data.payment_status === "failed") {
          toast.error("Payment didn't go through. Your session isn't booked yet.");
        }
      } catch (_) {
        toast.error("Couldn't verify your payment. If you were charged, contact hello@clinq.africa.");
      }
      loadSessions();
      setSearchParams({}, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const latest = sessions[0];
  const latestMeta = latest ? (SESSION_STATUS_META[latest.status] || null) : null;

  return (
    <AppShell nav={isProject ? PROJECT_NAV : NAV}>
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Welcome aboard</p>
          <h1 className="text-3xl font-bold text-neutral-900 sm:text-4xl" style={{ fontFamily: "Manrope, sans-serif" }}>
            {isProject ? "How to use Clinq for your project" : "How to use Clinq"}
          </h1>
          <p className="max-w-2xl text-sm text-neutral-600 sm:text-base">
            {isProject
              ? "A short walkthrough of the Project Readiness pathway — and what to do in your first week."
              : "A short walkthrough of every section of your Funder-Readiness dashboard — and what to do in your first week."}
          </p>
        </div>

        {/* Interactive tour CTA — the real walkthrough (business dashboard only) */}
        {!isProject && (
        <div className="mt-10 overflow-hidden rounded-3xl border border-[#0A8B85]/20 bg-gradient-to-br from-[#0A8B85]/[0.05] via-white to-[#F59E0B]/[0.05] p-6 sm:p-8" data-testid="tutorial-tour-card">
          <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#0A8B85] text-white shadow-[0_10px_25px_-10px_rgba(10,139,133,0.6)]">
                <Compass className="h-6 w-6" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Interactive tour</p>
                <h3 className="mt-1 text-xl font-bold text-neutral-900 sm:text-2xl" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Take the guided 60-second tour
                </h3>
                <p className="mt-1.5 max-w-xl text-sm text-neutral-600">
                  Step-by-step pop-ups on your actual dashboard — score, checklist, matches, team, and funder interest. Click <b>Replay</b> any time.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/founder/dashboard"
                data-testid="tutorial-start-tour"
                onClick={() => { resetFounderTour(); /* triggers auto-fire on next dashboard render */ }}
                className="inline-flex items-center gap-2 rounded-full bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-[#066667]"
              >
                <Play className="h-4 w-4 fill-current" /> Start tour
              </Link>
              <button
                type="button"
                data-testid="tutorial-replay-here"
                onClick={() => startFounderTour()}
                className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-sm font-bold text-neutral-700 hover:border-[#0A8B85] hover:text-[#0A8B85]"
              >
                <Compass className="h-4 w-4" /> Replay here
              </button>
            </div>
          </div>
        </div>
        )}

        {isProject && (
        <div className="mt-10 overflow-hidden rounded-3xl border border-[#0A8B85]/20 bg-gradient-to-br from-[#0A8B85]/[0.05] via-white to-[#F59E0B]/[0.05] p-6 sm:p-8" data-testid="project-tour-card">
          <div className="flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-[#0A8B85] text-white">
                <Compass className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Take the guided tour
                </h3>
                <p className="mt-1.5 max-w-xl text-sm text-neutral-600">
                  Step-by-step pop-ups on your actual pages — the intake sections, running the analysis, and reading your score report.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/project/intake"
                data-testid="project-tour-intake"
                onClick={() => resetProjectIntakeTour()}
                className="inline-flex items-center gap-2 rounded-full bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-[#066667]"
              >
                <Play className="h-4 w-4 fill-current" /> Tour the intake
              </Link>
              <Link
                to="/project/report"
                data-testid="project-tour-report"
                onClick={() => resetProjectReportTour()}
                className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-sm font-bold text-neutral-700 hover:border-[#0A8B85] hover:text-[#0A8B85]"
              >
                <Compass className="h-4 w-4" /> Tour the report
              </Link>
            </div>
          </div>
        </div>
        )}

        {/* Chapter cards */}
        <div className="mt-12">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-[#0A8B85]" />
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0A8B85]">The 4-step playbook</p>
          </div>
          <h2 className="mt-2 text-2xl font-bold text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            What to do in your first week
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            {(isProject ? PROJECT_CHAPTERS : CHAPTERS).map(({ num, icon: Icon, title, body }) => (
              <div key={num} data-testid={`tutorial-chapter-${num}`}
                className="group rounded-2xl border border-neutral-200 bg-white p-6 transition-shadow hover:shadow-md">
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0A8B85]/10 text-[#0A8B85] transition-colors group-hover:bg-[#0A8B85] group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-400">Chapter {num}</p>
                    <h3 className="mt-1 text-lg font-bold text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                      {title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-600">{body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Talk to an expert — booking */}
        <div className="mt-12 rounded-2xl border border-[#0A8B85]/20 bg-gradient-to-br from-[#0A8B85]/[0.04] via-white to-[#F59E0B]/[0.04] p-6 sm:p-8" data-testid="tutorial-expert-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Need a hand?</p>
              <h3 className="mt-1 text-xl font-bold text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                Talk to a Clinq fundraising expert
              </h3>
              <p className="mt-1 max-w-xl text-sm text-neutral-600">
                Book a private 1-on-1 walkthrough of your dashboard and fundraising plan.
                <b> Your first session is free (30 mins)</b> — follow-up sessions are 60 minutes at KES 7,500.
              </p>
              {latestMeta && (
                <p className="mt-2 inline-flex items-center gap-2 text-xs" data-testid="tutorial-expert-latest">
                  <span className={`rounded-full px-2 py-0.5 font-bold ${latestMeta.cls}`}>{latestMeta.label}</span>
                  <span className="text-neutral-500">{latest.preferred_date} at {latest.preferred_time} · {latest.duration_mins} mins</span>
                </p>
              )}
            </div>
            <button type="button" onClick={() => setBookOpen(true)}
              data-testid="tutorial-book-expert-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-[#066667]">
              <CalendarClock className="h-4 w-4" /> Book a session
            </button>
          </div>
          {!isProject && (
          <p className="mt-4 border-t border-neutral-200/70 pt-3 text-xs text-neutral-500">
            Need your documents built instead?{" "}
            <LaunchPrepButton testid="tutorial-services-cta"
              prefillCompany={prep.company}
              prefillSector={prep.sector}
              prefillStage={prep.stage}
              prefillMissingDocs={prep.missing}
              className="inline align-baseline font-semibold text-[#0A8B85] hover:underline">
              Launch Clinq Prep →
            </LaunchPrepButton>
          </p>
          )}
        </div>

        <BookExpertModal open={bookOpen} onClose={() => setBookOpen(false)} onBooked={loadSessions} />
      </div>
    </AppShell>
  );
}
